import { useMemo, useState, type CSSProperties } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SegmentedControl, type SegmentedControlOption } from "@/components/ui/segmented-control";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  ACCESS_PRESETS,
  blockingRequirement,
  detectPreset,
  PERMISSION_GROUPS,
  permissionsFor,
  presetPermissions,
  togglePermission,
  type AccessPreset,
  type ConfigurablePermission,
  type PermissionCatalog,
  type ProjectPermissionSet,
} from "@/lib/memberProjectPermissions";
import type { OrgFolderOption } from "@/stores/organizations/repository";
import {
  buildFolderIndex,
  folderCheckState,
  ProjectList,
  renderableFolderIds,
  type ProjectFolderSelection,
} from "./ProjectFolderPicker";
import type { PickerProject } from "./subcontractorAssignments";

/** What a member may do in each project, and how the editor changes it. */
export interface MemberPermissionsState {
  catalog: PermissionCatalog;
  /** Effective codes per project. A ticked project with no entry is at the default. */
  byProject: ReadonlyMap<number, ProjectPermissionSet>;
  onChange: (projectId: number, next: Set<ConfigurablePermission>) => void;
}

export interface MemberAccessEditorProps {
  projects: PickerProject[];
  folders: OrgFolderOption[];
  selection: ProjectFolderSelection;
  /**
   * `null` when per-project permissions are not available (the database
   * predates them, or they failed to load); the editor then offers project and
   * folder access only and shows `permissionsNotice`.
   */
  permissions: MemberPermissionsState | null;
  permissionsNotice?: string;
  emptyMessage: string;
  disabled?: boolean;
  /**
   * Height of the project list from `md` up, as a CSS length (the settings pane
   * is that plus the list's heading). The dialogs size it to the viewport — what
   * sits above the editor differs per dialog — so the modal never grows past
   * the window. Defaults to 26rem.
   */
  paneHeight?: string;
}

function firstSelectedId(projects: PickerProject[], selected: Set<number>): number | null {
  return projects.find((project) => selected.has(project.id))?.id ?? null;
}

function useMemberProjectSelection(
  projects: PickerProject[],
  folders: OrgFolderOption[],
  selection: ProjectFolderSelection
) {
  const folderIndex = useMemo(() => buildFolderIndex(folders), [folders]);

  const [activeProjectId, setActiveProjectId] = useState<number | null>(() =>
    firstSelectedId(projects, selection.selectedProjectIds)
  );

  // Projects ticked at some point while the editor is open, the seeded ones
  // included. A project's FIRST tick grants all of its folders; a re-tick after
  // an untick keeps whatever the admin had narrowed it to.
  const [everSelected] = useState(() => new Set(selection.selectedProjectIds));

  const selectAllFolders = (projectId: number) => {
    const roots = renderableFolderIds(folderIndex.byProject.get(projectId) ?? [], folderIndex);
    for (const folderId of roots) {
      // Toggling a folder that is not fully ticked restores its whole subtree.
      if (folderCheckState(folderId, selection.excludedFolderIds, folderIndex) !== true) {
        selection.onToggleFolder(folderId);
      }
    }
  };

  const toggleProject = (projectId: number) => {
    const ticking = !selection.selectedProjectIds.has(projectId);
    // Ticking a project opens its settings: what they may do there is the
    // next question, and it should be answered while the project is in mind.
    if (ticking) setActiveProjectId(projectId);
    if (ticking && !everSelected.has(projectId)) {
      everSelected.add(projectId);
      selectAllFolders(projectId);
    }
    selection.onToggleProject(projectId);
  };

  // Choosing an unticked project selects it as well: an open project nobody
  // can configure would only say "tick it first".
  const activateProject = (projectId: number) => {
    if (!selection.selectedProjectIds.has(projectId)) toggleProject(projectId);
    else setActiveProjectId(projectId);
  };

  return { folderIndex, activeProjectId, toggleProject, activateProject };
}

/**
 * Projects on the left — the open one with its folders beneath it — and the
 * chosen project's settings on the right: an access level preset and a switch
 * per capability.
 *
 * Presets are shortcuts over the switches, never a separate state — the level
 * shown is always derived from the switches, so "Custom" appears by itself the
 * moment the two disagree, and picking a preset simply sets the switches.
 */
function MemberProjectList(
  props: MemberAccessEditorProps & ReturnType<typeof useMemberProjectSelection>
) {
  const {
    projects,
    selection,
    permissions,
    emptyMessage,
    disabled,
    folderIndex,
    activeProjectId,
    toggleProject,
    activateProject,
  } = props;
  const { t } = useLanguage();
  const copy = t.settings.memberAccess;
  const projectBadge = permissions
    ? (projectId: number) => <ProjectAccessBadge permissions={permissions} projectId={projectId} />
    : undefined;

  return (
    <div className="min-w-0 space-y-2">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {copy.projectsHeading}
      </p>
      <ProjectList
        projects={projects}
        selectedProjectIds={selection.selectedProjectIds}
        onToggleProject={toggleProject}
        activeProjectId={activeProjectId}
        onActivateProject={activateProject}
        projectBadge={projectBadge}
        folders={{
          index: folderIndex,
          excludedFolderIds: selection.excludedFolderIds,
          onToggleFolder: selection.onToggleFolder,
        }}
        emptyMessage={emptyMessage}
        disabled={disabled}
        className="md:h-[var(--member-access-pane-h)]"
      />
    </div>
  );
}

function ProjectAccessBadge({
  permissions,
  projectId,
}: {
  permissions: MemberPermissionsState;
  projectId: number;
}) {
  const { t } = useLanguage();
  const preset = detectPreset(permissionsFor(permissions, projectId), permissions.catalog);
  return (
    <Badge variant="outline" className="font-normal">
      {t.settings.memberAccess.presets[preset]}
    </Badge>
  );
}

interface ProjectSettingsPanelProps {
  activeProject: PickerProject | null;
  activeIsSelected: boolean;
  permissions: MemberPermissionsState | null;
  permissionsNotice?: string;
  selectedProjectIds: Set<number>;
  disabled?: boolean;
  hasProjects: boolean;
}
function ProjectSettingsPanel(props: ProjectSettingsPanelProps) {
  const {
    activeProject,
    activeIsSelected,
    permissions,
    permissionsNotice,
    selectedProjectIds,
    disabled,
    hasProjects,
  } = props;
  const { t } = useLanguage();
  const copy = t.settings.memberAccess;
  return (
    <div className="min-w-0">
      {activeProject && activeIsSelected ? (
        <ScrollArea className="rounded-md border md:h-[calc(var(--member-access-pane-h)_+_1.75rem)]">
          <div className="space-y-5 p-4">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">{activeProject.name}</p>
              <p className="text-xs text-muted-foreground">{activeProject.buildingName}</p>
            </div>

            {permissions ? (
              <ProjectPermissionsPane
                projectId={activeProject.id}
                permissions={permissions}
                selectedProjectIds={selectedProjectIds}
                disabled={disabled}
              />
            ) : (
              permissionsNotice && (
                <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                  {permissionsNotice}
                </p>
              )
            )}
          </div>
        </ScrollArea>
      ) : (
        hasProjects && (
          <div className="flex h-full min-h-32 items-center justify-center rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            {copy.noProjectSelected}
          </div>
        )
      )}
    </div>
  );
}

interface ProjectPermissionsPaneProps {
  projectId: number;
  permissions: MemberPermissionsState;
  selectedProjectIds: Set<number>;
  disabled?: boolean;
}

function PermissionPresetControl({
  preset,
  applyPreset,
  presetOptions,
  disabled,
}: {
  preset: AccessPreset;
  applyPreset: (preset: AccessPreset) => void;
  presetOptions: SegmentedControlOption<AccessPreset>[];
  disabled?: boolean;
}) {
  const { t } = useLanguage();
  const copy = t.settings.memberAccess;
  return (
    <section className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {copy.accessLevel}
      </p>
      <SegmentedControl
        value={preset}
        onValueChange={applyPreset}
        options={presetOptions}
        disabled={disabled}
        aria-label={copy.accessLevel}
        className="flex w-full [&>button]:flex-1 [&>button]:justify-center"
        variant="bare"
        tone="brand"
      />
      <p className="text-xs text-muted-foreground">{copy.presetDescriptions[preset]}</p>
    </section>
  );
}

function ProjectPermissionsPane({
  projectId,
  permissions,
  selectedProjectIds,
  disabled,
}: ProjectPermissionsPaneProps) {
  const { t } = useLanguage();
  const copy = t.settings.memberAccess;
  const { catalog, onChange } = permissions;
  const current = permissionsFor(permissions, projectId);
  const preset = detectPreset(current, catalog);
  const inCatalog = new Set(catalog.map((entry) => entry.code));

  const presetOptions: SegmentedControlOption<AccessPreset>[] = [
    ...ACCESS_PRESETS.map((value) => ({ value, label: copy.presets[value] })),
    // Shown, never chosen: it names the state the switches are in.
    { value: "custom", label: copy.presets.custom, disabled: preset !== "custom" },
  ];

  const applyPreset = (next: AccessPreset) => {
    if (next === "custom") return;
    onChange(projectId, presetPermissions(next, catalog));
  };

  const otherSelected = Array.from(selectedProjectIds).filter((id) => id !== projectId);

  return (
    <>
      <PermissionPresetControl
        preset={preset}
        applyPreset={applyPreset}
        presetOptions={presetOptions}
        disabled={disabled}
      />
      <section className="space-y-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {copy.whatTheyCanDo}
        </p>
        {PERMISSION_GROUPS.map((group) => {
          const codes = group.codes.filter((code) => inCatalog.has(code));
          if (codes.length === 0) return null;
          return (
            <div key={group.key} className="space-y-2">
              <p className="text-sm font-medium">{copy.groups[group.key]}</p>
              <div className="space-y-3">
                {codes.map((code) => {
                  const switchId = `member-permission-${projectId}-${code}`;
                  const blockedBy = blockingRequirement(current, code, catalog);
                  return (
                    <div key={code} className="flex items-start gap-3">
                      <Switch
                        id={switchId}
                        checked={current.has(code)}
                        disabled={disabled}
                        onCheckedChange={(on) =>
                          onChange(projectId, togglePermission(current, code, on, catalog))
                        }
                        className="mt-0.5"
                      />
                      <div className="min-w-0 space-y-0.5">
                        <Label htmlFor={switchId} className="cursor-pointer">
                          {copy.permissions[code].label}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {copy.permissions[code].description}
                          {blockedBy && (
                            <>
                              {" "}
                              {copy.requires.replace(
                                "{permission}",
                                copy.permissions[blockedBy].label
                              )}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {otherSelected.length > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => {
            for (const id of otherSelected) onChange(id, new Set(current));
            toast.success(copy.appliedToAll);
          }}
        >
          {copy.applyToAll}
        </Button>
      )}
    </>
  );
}

export function MemberAccessEditor(props: MemberAccessEditorProps) {
  const {
    projects,
    folders,
    selection,
    permissions,
    permissionsNotice,
    emptyMessage,
    disabled,
    paneHeight = "26rem",
  } = props;
  const selectionState = useMemberProjectSelection(projects, folders, selection);
  const { activeProjectId } = selectionState;

  const activeProject = projects.find((project) => project.id === activeProjectId) ?? null;
  const activeIsSelected =
    activeProject !== null && selection.selectedProjectIds.has(activeProject.id);

  return (
    <div
      className="grid min-w-0 gap-4 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
      style={{ "--member-access-pane-h": paneHeight } as CSSProperties}
    >
      <MemberProjectList {...props} {...selectionState} />

      <ProjectSettingsPanel
        activeProject={activeProject}
        activeIsSelected={activeIsSelected}
        permissions={permissions}
        permissionsNotice={permissionsNotice}
        selectedProjectIds={selection.selectedProjectIds}
        disabled={disabled}
        hasProjects={projects.length > 0}
      />
    </div>
  );
}
